import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';

import StraightenIcon from '@mui/icons-material/Straighten';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import LayersIcon from '@mui/icons-material/Layers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CropIcon from '@mui/icons-material/Crop';
import TimelineIcon from '@mui/icons-material/Timeline';
import PushPinIcon from '@mui/icons-material/PushPin';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import OpenWithIcon from '@mui/icons-material/OpenWith';

import { COLORS, TOOLTIP_STYLES } from '../styles/viewerStyles';

const COLLAPSE_KEY = 'viewerToolbar_collapsed';

function ViewerToolbar({
  hasGaussianOnly,
  gaussianSplattingId,
  resultsAssetId,
  panelStates,
  onTogglePanel,
  onExit,
  showTransformation = true,
}) {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(COLLAPSE_KEY);
      if (stored !== null) return stored === 'true';
      return window.innerWidth <= 600;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(isCollapsed));
    } catch { /* noop */ }
  }, [isCollapsed]);

  const toolbarBg = COLORS.darkBg;

  const getButtonSx = (isOpen) => ({
    color: COLORS.white,
    borderRadius: '12px',
    width: 36,
    height: 36,
    transition: 'all 0.2s ease-in-out',
    bgcolor: isOpen ? COLORS.selectedLight : 'transparent',
    '&:hover': {
      bgcolor: isOpen ? COLORS.highlightedBgHover : COLORS.hoverLight,
      transform: 'scale(1.05)',
    },
  });

  const [hoveredKey, setHoveredKey] = useState(null);

  const renderTooltip = (titleKey, descKey, isOpen, children) => (
    <Tooltip
      placement="bottom"
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
      open={!isOpen && hoveredKey === titleKey}
      slotProps={{
        tooltip: { sx: TOOLTIP_STYLES.tooltip },
        arrow: { sx: TOOLTIP_STYLES.arrow },
        popper: {
          onMouseEnter: () => setHoveredKey(null),
        },
      }}
      title={
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>{t(titleKey)}</Typography>
          {descKey && (
            <Typography sx={{ fontSize: '0.7rem', opacity: 0.8, mt: 0.25, color: '#fff' }}>{t(descKey)}</Typography>
          )}
        </Box>
      }
    >
      <span
        onMouseEnter={() => setHoveredKey(titleKey)}
        onMouseLeave={() => setHoveredKey(null)}
        onClick={() => setHoveredKey(null)}
      >
        {children}
      </span>
    </Tooltip>
  );

  const verticalDivider = (
    <Divider
      orientation="vertical"
      flexItem
      sx={{ mx: 0.5, borderColor: 'rgba(255,255,255,0.12)', alignSelf: 'center', height: 20 }}
    />
  );

  if (isCollapsed) {
    return (
      <Box sx={{
        position: 'absolute',
        top: 'calc(12px + env(safe-area-inset-top, 0px))',
        left: 12,
        zIndex: 1200,
      }}>
        <IconButton
          onClick={() => setIsCollapsed(false)}
          sx={{
            bgcolor: toolbarBg,
            backdropFilter: 'blur(16px)',
            border: `1px solid ${COLORS.panelBorder}`,
            boxShadow: COLORS.panelShadow,
            borderRadius: '16px',
            color: COLORS.white,
            width: 40,
            height: 40,
            '&:hover': { bgcolor: 'rgba(20,20,30,0.9)' },
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'absolute',
      top: 'calc(12px + env(safe-area-inset-top, 0px))',
      left: 12,
      zIndex: 1200,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0.5,
      maxWidth: 'calc(100vw - 24px)',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      bgcolor: toolbarBg,
      backdropFilter: 'blur(16px)',
      border: `1px solid ${COLORS.panelBorder}`,
      boxShadow: COLORS.panelShadow,
      borderRadius: '16px',
      px: 1,
      py: 0.5,
    }}>
      {/* Base Layers — always leftmost */}
      {renderTooltip('BaseLayers', 'ToolbarLayersDesc', panelStates.baseLayers,
        <IconButton onClick={() => onTogglePanel('baseLayers')} sx={getButtonSx(panelStates.baseLayers)}>
          <LayersIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
      {verticalDivider}

      {/* Analysis Group */}
      {!hasGaussianOnly && (
        renderTooltip('MeasurementTools', 'ToolbarMeasurementDesc', panelStates.measurement,
          <IconButton onClick={() => onTogglePanel('measurement')} sx={getButtonSx(panelStates.measurement)}>
            <StraightenIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )
      )}

      {!hasGaussianOnly && (
        renderTooltip('Profiler', 'ToolbarProfilerDesc', panelStates.profiler,
          <IconButton onClick={() => onTogglePanel('profiler')} sx={getButtonSx(panelStates.profiler)}>
            <TimelineIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )
      )}

      {/* Divider between Analysis and Visualization */}
      {!hasGaussianOnly && verticalDivider}

      {/* Visualization Group */}
      {gaussianSplattingId && !hasGaussianOnly && (
        renderTooltip('GaussianSplatting', 'ToolbarGaussianDesc', panelStates.gaussian,
          <IconButton onClick={() => onTogglePanel('gaussian')} sx={getButtonSx(panelStates.gaussian)}>
            <BlurOnIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )
      )}

      {resultsAssetId && resultsAssetId.length > 0 && (
        renderTooltip('Products', 'ToolbarModelsDesc', panelStates.models3D,
          <IconButton onClick={() => onTogglePanel('models3D')} sx={getButtonSx(panelStates.models3D)}>
            <FilePresentIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )
      )}

      {renderTooltip('Viewpoints', 'ToolbarViewpointsDesc', panelStates.viewpoints,
        <IconButton onClick={() => onTogglePanel('viewpoints')} sx={getButtonSx(panelStates.viewpoints)}>
          <VisibilityIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {renderTooltip('Annotations', 'ToolbarAnnotationsDesc', panelStates.annotations,
        <IconButton onClick={() => onTogglePanel('annotations')} sx={getButtonSx(panelStates.annotations)}>
          <PushPinIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {/* Clipping Planes in Visualization group */}
      {!hasGaussianOnly && (
        renderTooltip('ClippingPlanes', 'ToolbarClippingDesc', panelStates.clippingPlane,
          <IconButton onClick={() => onTogglePanel('clippingPlane')} sx={getButtonSx(panelStates.clippingPlane)}>
            <CropIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )
      )}

      {/* Divider between Visualization and System */}
      {verticalDivider}

      {/* System Group */}
      {showTransformation && renderTooltip('TransformationTool', 'ToolbarTransformationDesc', panelStates.transformation,
        <IconButton onClick={() => onTogglePanel('transformation')} sx={getButtonSx(panelStates.transformation)}>
          <OpenWithIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {renderTooltip('SettingsCesium', 'ToolbarSettingsDesc', panelStates.settings,
        <IconButton onClick={() => onTogglePanel('settings')} sx={getButtonSx(panelStates.settings)}>
          <SettingsIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {renderTooltip('ExitViewer', null, false,
        <IconButton onClick={onExit} sx={getButtonSx(false)}>
          <ExitToAppIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {/* Collapse chevron */}
      <IconButton
        onClick={() => setIsCollapsed(true)}
        sx={{
          color: 'rgba(255,255,255,0.5)',
          width: 24,
          height: 24,
          ml: 0.25,
          '&:hover': { color: COLORS.white },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

ViewerToolbar.propTypes = {
  hasGaussianOnly: PropTypes.bool,
  gaussianSplattingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resultsAssetId: PropTypes.array,
  panelStates: PropTypes.shape({
    measurement: PropTypes.bool,
    gaussian: PropTypes.bool,
    models3D: PropTypes.bool,
    viewpoints: PropTypes.bool,
    settings: PropTypes.bool,
    baseLayers: PropTypes.bool,
    clippingPlane: PropTypes.bool,
    profiler: PropTypes.bool,
    annotations: PropTypes.bool,
    transformation: PropTypes.bool,
  }).isRequired,
  onTogglePanel: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  showTransformation: PropTypes.bool,
};

export default ViewerToolbar;
